import React, { useMemo } from "react";
import { User } from "../../types/Session";
import { ChartPanel } from "./Board.styles";
import Icon from "../ui/Icon";

const W = 320;
const H = 160;
const PAD = { top: 10, right: 62, bottom: 22, left: 26 };

const short = (name = "") => (name.length > 9 ? `${name.slice(0, 8)}…` : name);

const length = (points: number[][]) =>
  points.reduce(
    (total, [x, y], i) =>
      i === 0
        ? 0
        : total + Math.hypot(x - points[i - 1][0], y - points[i - 1][1]),
    0,
  );

/**
 * Final-ranking chart: one trail per player, in their own seat color, drawing
 * the running total after each scored round. Identity is color, so every trail
 * also carries the player's name at its last point (principle 4).
 */
const ScoreChart: React.FC<{ players: Partial<User>[] }> = ({ players }) => {
  const rounds = Math.max(0, ...players.map((p) => p.history?.length || 0));

  const trails = useMemo(() => {
    if (rounds < 2) return [];

    const max = Math.max(1, ...players.flatMap((p) => p.history || []));
    const x = (i: number) =>
      PAD.left + (i / (rounds - 1)) * (W - PAD.left - PAD.right);
    const y = (v: number) =>
      H - PAD.bottom - (v / max) * (H - PAD.top - PAD.bottom);

    return players
      .map((player) => {
        const history = player.history || [];
        // late joiners have fewer rounds, align them to the last round
        const offset = rounds - history.length;
        const points = history.map((v, i) => [x(i + offset), y(v)]);
        return { player, history, offset, points };
      })
      .filter(({ points }) => points.length > 0);
  }, [players, rounds]);

  const ticks = useMemo(() => {
    const max = Math.max(1, ...players.flatMap((p) => p.history || []));
    const y = (v: number) =>
      H - PAD.bottom - (v / max) * (H - PAD.top - PAD.bottom);
    return Array.from(new Set([0, Math.round(max / 2), max])).map((t) => ({
      value: t,
      y: y(t),
    }));
  }, [players]);

  // a single round is a bar chart, not an evolution: the ranking already tells it
  if (rounds < 2) return null;

  return (
    <ChartPanel>
      <h3>
        <Icon name="sparkles" size={18} />
        Pontos por rodada
      </h3>

      <svg
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={`Evolução dos pontos ao longo de ${rounds} rodadas`}
      >
        {ticks.map(({ value, y }) => (
          <g key={value}>
            <line
              className="grid"
              x1={PAD.left}
              x2={W - PAD.right}
              y1={y}
              y2={y}
            />
            <text
              className="axis"
              x={PAD.left - 6}
              y={y}
              textAnchor="end"
              dominantBaseline="middle"
            >
              {value}
            </text>
          </g>
        ))}

        <text
          className="axis"
          x={(PAD.left + W - PAD.right) / 2}
          y={H - 4}
          textAnchor="middle"
        >
          Rodada
        </text>

        {trails.map(({ player, history, offset, points }) => {
          const [lastX, lastY] = points[points.length - 1];

          return (
            <g key={player.id || player.name}>
              <polyline
                className="trail"
                style={{ "--len": length(points) } as React.CSSProperties}
                points={points.map((p) => p.join(",")).join(" ")}
                stroke={player.color}
              />
              {points.map(([px, py], i) => (
                <circle
                  key={i}
                  className="dot"
                  cx={px}
                  cy={py}
                  r={3.5}
                  fill={player.color}
                >
                  <title>
                    {`${player.name} · rodada ${i + offset + 1}: ${history[i]}`}
                  </title>
                </circle>
              ))}
              <text
                className="name"
                x={lastX + 7}
                y={lastY}
                dominantBaseline="middle"
              >
                {short(player.name)}
              </text>
            </g>
          );
        })}
      </svg>
    </ChartPanel>
  );
};

export default ScoreChart;
